import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  MdPerson,
  MdArrowBack,
  MdBarChart,
  MdPieChart,
  MdAssignment,
  MdTrendingUp,
  MdSearch,
  MdRefresh,
  MdSupervisorAccount,
  MdEngineering,
  MdExpandMore,
  MdExpandLess,
  MdCalendarToday,
  MdDomain,
} from "react-icons/md";
import { useWorkers, ROLES } from "context/WorkersContext";
import { getWorkerReport } from "api/kpiReports";
import Chart from "react-apexcharts";

const PRIMARY = "#0e76a8";
const SECONDARY = "#f27405";

/* ── helper: colour for score range 1-5 ── */
function scoreColor(score) {
  if (score >= 5) return "#10b981";
  if (score >= 4) return "#22c55e";
  if (score >= 3) return "#eab308";
  if (score >= 2) return "#f59e0b";
  return "#ef4444";
}

/* ── helper: percentage badge colour ── */
function pctColor(pct) {
  if (pct >= 80) return "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20";
  if (pct >= 60) return "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20";
  return "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20";
}

/* ── helper: map weighted percentage to performance label ── */
function performanceLabel(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return "—";
  const n = Number(pct);
  if (n >= 90) return "Excellent";
  if (n >= 75) return "Very Good";
  if (n >= 60) return "Good";
  return "Bad";
}

/* ── helper: role badge ── */
function RoleBadge({ role }) {
  const config = {
    supervisor: { icon: MdSupervisorAccount, style: "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" },
    operator: { icon: MdEngineering, style: "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300" },
    admin: { icon: MdPerson, style: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300" },
  };
  const { icon: Icon, style } = config[role] || config.operator;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wider ${style}`}>
      <Icon className="text-sm" />
      {role}
    </span>
  );
}

/* ── Score Ring ── */
function ScoreRing({ percentage, size = 120 }) {
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;
  const color = percentage >= 80 ? "#10b981" : percentage >= 60 ? "#f59e0b" : "#ef4444";

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={8}
          className="text-slate-100 dark:text-slate-800"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={8}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-black text-slate-900 dark:text-white">{percentage.toFixed(1)}%</span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">weighted</span>
      </div>
    </div>
  );
}

/* ── Evaluation card (expandable) ── */
function EvaluationCard({ evaluation, index }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
      {/* Header row */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between gap-4 p-4 sm:p-5 text-left hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="flex items-center justify-center w-9 h-9 rounded-xl text-white text-sm font-black shrink-0"
            style={{ backgroundColor: PRIMARY }}
          >
            {index + 1}
          </span>
          <div className="min-w-0">
            <p className="font-bold text-slate-900 dark:text-white text-sm truncate">
              {evaluation.sector_name}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <MdCalendarToday className="text-xs text-slate-400 shrink-0" />
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {new Date(evaluation.evaluated_at).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
                {" at "}
                {new Date(evaluation.evaluated_at).toLocaleTimeString("en-GB", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          </div>
        </div>
          <div className="flex items-center gap-3 shrink-0">
          <span className={`px-3 py-1 rounded-lg text-xs font-black ${pctColor(evaluation.weighted_percentage)}`}>
            {performanceLabel(evaluation.weighted_percentage)}
          </span>
          {expanded ? (
            <MdExpandLess className="text-xl text-slate-400" />
          ) : (
            <MdExpandMore className="text-xl text-slate-400" />
          )}
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-slate-100 dark:border-slate-800 p-4 sm:p-5 space-y-4">
          {/* Summary chips */}
          <div className="flex flex-wrap gap-3">
            <div className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800/50">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">KPIs evaluated</p>
              <p className="text-lg font-black text-slate-900 dark:text-white">{evaluation.kpi_count}</p>
            </div>
            <div className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800/50">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total weight</p>
              <p className="text-lg font-black text-slate-900 dark:text-white">{evaluation.total_weight}</p>
            </div>
            <div className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800/50">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Eval ID</p>
              <p className="text-sm font-mono font-bold text-slate-700 dark:text-slate-300 mt-0.5">{evaluation.evaluation_id}</p>
            </div>
          </div>

          {/* KPI scores */}
          <div className="space-y-2">
            {evaluation.scores.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between gap-4 p-3 rounded-xl bg-slate-50/80 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate">{s.kpi_name}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {/* Score range badge */}
                  <span
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-black text-white"
                    style={{ backgroundColor: scoreColor(s.score_range) }}
                  >
                    {s.score_range}
                  </span>
                  {/* Percentage bar */}
                  <div className="w-24 sm:w-32">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-[11px] font-bold text-slate-500">{s.score_percentage}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.min(100, s.score_percentage)}%`,
                          backgroundColor: scoreColor(s.score_range),
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Main component
   ═══════════════════════════════════════════════════════════════════ */
export default function WorkerReport({ onBack }) {
  const { workers } = useWorkers();
  const [selectedWorkerId, setSelectedWorkerId] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* Fetch report when worker is selected */
  const fetchReport = useCallback(async (workerId, fromDateParam, toDateParam) => {
    if (!workerId) {
      setReport(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await getWorkerReport(workerId, fromDateParam, toDateParam);
      setReport(result.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load report");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedWorkerId) fetchReport(selectedWorkerId, fromDate, toDate);
  }, [selectedWorkerId, fetchReport]);

  /* Filter workers list for dropdown */
  const filteredWorkers = useMemo(() => {
    const nonAdmin = workers.filter((w) => w.role !== ROLES.ADMIN);
    if (!search.trim()) return nonAdmin;
    const q = search.trim().toLowerCase();
    return nonAdmin.filter(
      (w) =>
        w.name.toLowerCase().includes(q) ||
        (w.employeeId && w.employeeId.toLowerCase().includes(q)) ||
        (w.sector_name && w.sector_name.toLowerCase().includes(q))
    );
  }, [workers, search]);

  /* Chart data for KPI bar chart across all evaluations */
  const kpiChartData = useMemo(() => {
    if (!report?.evaluations?.length) return null;
    // Aggregate all KPI scores across evaluations
    const kpiMap = {};
    report.evaluations.forEach((ev) => {
      ev.scores.forEach((s) => {
        if (!kpiMap[s.kpi_id]) {
          kpiMap[s.kpi_id] = { name: s.kpi_name, totalPct: 0, totalRange: 0, count: 0 };
        }
        kpiMap[s.kpi_id].totalPct += s.score_percentage;
        kpiMap[s.kpi_id].totalRange += s.score_range;
        kpiMap[s.kpi_id].count += 1;
      });
    });
    const kpis = Object.values(kpiMap);
    return {
      categories: kpis.map((k) => k.name.length > 20 ? k.name.slice(0, 18) + "..." : k.name),
      percentages: kpis.map((k) => Math.round(k.totalPct / k.count)),
      ranges: kpis.map((k) => Math.round((k.totalRange / k.count) * 10) / 10),
    };
  }, [report]);

  /* Pie chart for sector distribution */
  const sectorPieData = useMemo(() => {
    if (!report?.evaluations?.length) return null;
    const sectorCounts = {};
    report.evaluations.forEach((ev) => {
      sectorCounts[ev.sector_name] = (sectorCounts[ev.sector_name] || 0) + 1;
    });
    return {
      labels: Object.keys(sectorCounts),
      series: Object.values(sectorCounts),
    };
  }, [report]);

  const worker = report?.worker;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            <MdArrowBack className="text-lg" />
            Back
          </button>
          <div>
            <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
              Worker Report
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
              Select a worker to view their performance report
            </p>
          </div>
        </div>
        {selectedWorkerId && report && (
          <button
            type="button"
            onClick={() => fetchReport(selectedWorkerId)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            <MdRefresh className={`text-lg ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        )}
      </div>

      {/* Worker selector */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-4 sm:p-5">
        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-3">
          Select a worker
        </label>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-0">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, ID, or sector..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm placeholder-slate-400 focus:ring-2 focus:ring-[#0e76a8]/30 focus:outline-none"
            />
          </div>
          <select
            value={selectedWorkerId}
            onChange={(e) => setSelectedWorkerId(e.target.value)}
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm px-4 py-2.5 min-w-[240px] focus:ring-2 focus:ring-[#0e76a8]/30 focus:outline-none"
          >
            <option value="">Choose worker...</option>
            {filteredWorkers.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} ({w.role}{w.sector_name ? ` - ${w.sector_name}` : ""})
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2 mt-2 sm:mt-0">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm px-3 py-2"
              aria-label="From date"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm px-3 py-2"
              aria-label="To date"
            />
            <button
              type="button"
              onClick={() => fetchReport(selectedWorkerId, fromDate, toDate)}
              disabled={!selectedWorkerId}
              className="px-4 py-2 rounded-xl bg-[#0e76a8] text-white font-bold text-sm disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-16 text-center">
          <div className="h-10 w-10 mx-auto animate-spin rounded-full border-4 border-slate-200 dark:border-slate-700 border-t-[#0e76a8] mb-4" />
          <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Loading report...</p>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-5 flex items-center justify-between gap-4">
          <p className="text-red-600 dark:text-red-400 text-sm font-medium">{error}</p>
          <button
            type="button"
            onClick={() => fetchReport(selectedWorkerId)}
            className="px-4 py-2 rounded-xl bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-sm font-bold hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!selectedWorkerId && !loading && (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30 p-16 text-center">
          <MdPerson className="mx-auto text-5xl text-slate-300 dark:text-slate-600 mb-4" />
          <p className="text-slate-500 dark:text-slate-400 font-medium text-sm">
            Select a worker above to view their evaluation report
          </p>
        </div>
      )}

      {/* No evaluations / no data */}
      {selectedWorkerId && !loading && (!report || report.evaluations?.length === 0) && (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30 p-16 text-center">
          <MdAssignment className="mx-auto text-5xl text-slate-300 dark:text-slate-600 mb-4" />
          <p className="text-slate-500 dark:text-slate-400 font-medium text-sm">
            No evaluation scores found for this worker
          </p>
        </div>
      )}

      {/* Report content */}
      {report && worker && !loading && report.evaluations?.length > 0 && (
        <>
          {/* Worker profile + overall score */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile */}
            <div className="lg:col-span-2 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-5 sm:p-6">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-black" style={{ backgroundColor: PRIMARY }}>
                  {worker.evaluatee_name?.charAt(0)?.toUpperCase() || "W"}
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900 dark:text-white">{worker.evaluatee_name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <RoleBadge role={worker.evaluatee_role} />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Evaluations</p>
                  <p className="text-xl font-black text-slate-900 dark:text-white">{report.evaluations.length}</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">KPIs scored</p>
                  <p className="text-xl font-black text-slate-900 dark:text-white">{report.overall_kpi_count}</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Total weight</p>
                  <p className="text-xl font-black text-slate-900 dark:text-white">{report.overall_total_weight}</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Sectors</p>
                  <p className="text-xl font-black text-slate-900 dark:text-white">
                    {new Set(report.evaluations.map((e) => e.sector_name)).size}
                  </p>
                </div>
              </div>
            </div>

            {/* Overall score ring */}
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-5 sm:p-6 flex flex-col items-center justify-center">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">Overall performance</p>
              <ScoreRing percentage={report.overall_weighted_percentage} size={140} />
                        <p className="text-sm font-bold text-slate-600 dark:text-slate-400 mt-3">
                          {performanceLabel(report.overall_weighted_percentage)}
                        </p>
            </div>
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* KPI bar chart */}
            {kpiChartData && (
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-5 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <MdBarChart className="text-xl" style={{ color: PRIMARY }} />
                  <h3 className="font-black text-slate-900 dark:text-white">Average KPI Scores (%)</h3>
                </div>
                <div className="h-72">
                  <Chart
                    type="bar"
                    width="100%"
                    height="100%"
                    series={[{ name: "Avg %", data: kpiChartData.percentages }]}
                    options={{
                      chart: { toolbar: { show: false } },
                      plotOptions: { bar: { borderRadius: 8, columnWidth: "55%", distributed: true } },
                      xaxis: {
                        categories: kpiChartData.categories,
                        labels: { style: { colors: "#64748b", fontSize: "11px" }, rotate: -30, trim: true },
                      },
                      yaxis: { min: 0, max: 100, labels: { style: { colors: "#64748b" } } },
                      colors: kpiChartData.percentages.map((p) =>
                        p >= 80 ? "#10b981" : p >= 60 ? "#f59e0b" : "#ef4444"
                      ),
                      dataLabels: { enabled: true, formatter: (v) => `${v}%`, style: { fontSize: "11px" } },
                      grid: { borderColor: "rgba(148,163,184,0.15)", strokeDashArray: 4 },
                      tooltip: { theme: "light", y: { formatter: (v) => `${v}%` } },
                      legend: { show: false },
                    }}
                  />
                </div>
              </div>
            )}

            {/* Sector distribution pie */}
            {sectorPieData && sectorPieData.series.length > 0 && (
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-5 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <MdPieChart className="text-xl" style={{ color: SECONDARY }} />
                  <h3 className="font-black text-slate-900 dark:text-white">Evaluations by Sector</h3>
                </div>
                <div className="h-72">
                  <Chart
                    type="donut"
                    width="100%"
                    height="100%"
                    series={sectorPieData.series}
                    options={{
                      labels: sectorPieData.labels,
                      colors: [PRIMARY, SECONDARY, "#10b981", "#8b5cf6", "#ec4899", "#06b6d4"],
                      legend: { position: "bottom", fontSize: "12px" },
                      dataLabels: { enabled: true },
                      plotOptions: {
                        pie: {
                          donut: { size: "55%", labels: { show: true, total: { show: true, label: "Total", fontSize: "14px", fontWeight: 800 } } },
                        },
                      },
                    }}
                  />
                </div>
              </div>
            )}

            {/* If only 1 sector, show a score trend instead */}
            {sectorPieData && sectorPieData.series.length <= 1 && report.evaluations.length > 1 && (
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-5 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <MdTrendingUp className="text-xl" style={{ color: SECONDARY }} />
                  <h3 className="font-black text-slate-900 dark:text-white">Performance Trend</h3>
                </div>
                <div className="h-72">
                  <Chart
                    type="line"
                    width="100%"
                    height="100%"
                    series={[
                      {
                        name: "Weighted %",
                        data: report.evaluations
                          .slice()
                          .sort((a, b) => new Date(a.evaluated_at) - new Date(b.evaluated_at))
                          .map((ev) => ev.weighted_percentage),
                      },
                    ]}
                    options={{
                      chart: { toolbar: { show: false } },
                      xaxis: {
                        categories: report.evaluations
                          .slice()
                          .sort((a, b) => new Date(a.evaluated_at) - new Date(b.evaluated_at))
                          .map((ev) =>
                            new Date(ev.evaluated_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
                          ),
                        labels: { style: { colors: "#64748b", fontSize: "11px" } },
                      },
                      yaxis: { min: 0, max: 100, labels: { style: { colors: "#64748b" } } },
                      colors: [PRIMARY],
                      stroke: { curve: "smooth", width: 3 },
                      markers: { size: 5, strokeWidth: 0 },
                      grid: { borderColor: "rgba(148,163,184,0.15)", strokeDashArray: 4 },
                      tooltip: { theme: "light", y: { formatter: (v) => `${v.toFixed(1)}%` } },
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Evaluation history */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <MdAssignment className="text-xl" style={{ color: PRIMARY }} />
              <h3 className="font-black text-slate-900 dark:text-white text-lg">
                Evaluation History
              </h3>
              <span className="ml-auto text-sm font-bold text-slate-400">
                {report.evaluations.length} evaluation{report.evaluations.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="space-y-3">
              {report.evaluations.map((ev, idx) => (
                <EvaluationCard key={ev.evaluation_id} evaluation={ev} index={idx} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
